import React, { useState, useEffect } from 'react';
import { WifiOff, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { networkStatus, getConnectionStatus, attemptConnectionRecovery } from '../lib/supabase';

const NetworkStatusIndicator = ({ className = '' }) => {
  const [isOnline, setIsOnline] = useState(true);
  const [connectionHealth, setConnectionHealth] = useState({ isHealthy: true, shouldRetry: true });
  const [isRecovering, setIsRecovering] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Initial status check
    const updateStatus = () => {
      const status = getConnectionStatus();
      setConnectionHealth(status);
      setIsOnline(status?.isOnline && status?.isHealthy);
    };

    updateStatus();

    // Monitor network changes
    const cleanup = networkStatus?.onConnectionChange((online) => {
      setIsOnline(online);
      updateStatus();
      
      // Auto-hide details when connection is restored
      if (online && showDetails) {
        setTimeout(() => setShowDetails(false), 3000);
      }
    });

    // Update status periodically
    const interval = setInterval(updateStatus, 10000); // Every 10 seconds

    return () => {
      cleanup?.();
      clearInterval(interval);
    };
  }, [showDetails]);

  const handleRecovery = async () => {
    setIsRecovering(true);
    try {
      const recovered = await attemptConnectionRecovery();
      if (recovered) {
        setIsOnline(true);
        setShowDetails(false);
      }
    } finally {
      setIsRecovering(false);
    }
  };

  const getStatusIcon = () => {
    if (isRecovering) {
      return <RefreshCw className="w-4 h-4 animate-spin" />;
    }
    
    if (isOnline && connectionHealth?.isHealthy) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    
    if (isOnline && !connectionHealth?.isHealthy) {
      return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    }
    
    return <WifiOff className="w-4 h-4 text-red-500" />;
  };

  const getStatusColor = () => {
    if (isOnline && connectionHealth?.isHealthy) return 'bg-green-500';
    if (isOnline && !connectionHealth?.isHealthy) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusText = () => {
    if (isRecovering) return 'Reconnecting...';
    if (isOnline && connectionHealth?.isHealthy) return 'Connected';
    if (isOnline && !connectionHealth?.isHealthy) return 'Unstable';
    return 'Offline';
  };

  // Only show if there are connection issues
  if (isOnline && connectionHealth?.isHealthy && !showDetails) {
    return null;
  }

  return (
    <div className={`fixed top-4 right-4 z-50 ${className}`}>
      <div 
        className={`
          bg-white dark:bg-gray-800 rounded-lg shadow-lg border 
          transition-all duration-300 cursor-pointer
          ${showDetails ? 'p-4 min-w-80' : 'p-2'}
        `}
        onClick={() => setShowDetails(!showDetails)}
      >
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
          {getStatusIcon()}
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {getStatusText()}
          </span>
          {!showDetails && (
            <span className="text-xs text-gray-500 ml-1">
              (click for details)
            </span>
          )}
        </div>

        {showDetails && (
          <div className="mt-3 space-y-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between">
                <span>Network Status:</span>
                <span className={isOnline ? 'text-green-600' : 'text-red-600'}>
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Service Health:</span>
                <span className={connectionHealth?.isHealthy ? 'text-green-600' : 'text-yellow-600'}>
                  {connectionHealth?.isHealthy ? 'Healthy' : 'Degraded'}
                </span>
              </div>
              {connectionHealth?.failureCount > 0 && (
                <div className="flex justify-between">
                  <span>Recent Failures:</span>
                  <span className="text-yellow-600">
                    {connectionHealth?.failureCount}
                  </span>
                </div>
              )}
            </div>

            {(!isOnline || !connectionHealth?.isHealthy) && (
              <div className="space-y-2">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {!isOnline && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded text-red-700 dark:text-red-300">
                      <strong>Connection Lost</strong><br />
                      Please check your internet connection
                    </div>
                  )}
                  {isOnline && !connectionHealth?.isHealthy && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded text-yellow-700 dark:text-yellow-300">
                      <strong>Unstable Connection</strong><br />
                      Some features may not work properly
                    </div>
                  )}
                </div>

                {connectionHealth?.shouldRetry && (
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleRecovery();
                    }}
                    disabled={isRecovering}
                    className={`
                      w-full px-3 py-2 text-sm rounded-md font-medium
                      transition-colors duration-200
                      ${isRecovering 
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed' :'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                      }
                    `}
                  >
                    {isRecovering ? (
                      <span className="flex items-center justify-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Reconnecting...
                      </span>
                    ) : (
                      'Try Reconnecting'
                    )}
                  </button>
                )}
              </div>
            )}

            <div className="text-xs text-gray-500 pt-2 border-t border-gray-200 dark:border-gray-600">
              Click outside to dismiss
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkStatusIndicator;